import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  InjectionToken,
  Injector,
  Input,
  input,
  model,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatSuffix, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { DateTimeAdapter, OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import moment from 'moment';
import { BaseFormControlDirective } from '../../directives/base-form-control.directive';
import { CULTURE_SERVICE, ICultureService } from '../../models/iculture-service.model';
import { DateValidators } from '../../validators/date.validators';
import { ControlErrorComponent } from '../control-error/control-error.component';

export const OWL_DATE_ADAPTER = new InjectionToken<DateTimeAdapter<any>>('OWL_DATE_ADAPTER');

@Component({
  selector: 'app-date-control',
  templateUrl: './date-control.component.html',
  styleUrls: ['./date-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateControlComponent),
      multi: true,
    },
    {
      provide: BaseFormControlDirective,
      useExisting: DateControlComponent,
    },
    {
      provide: DateTimeAdapter,
      useExisting: OWL_DATE_ADAPTER,
    },
  ],
  imports: [MatLabel, 
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    OwlDateTimeModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    ControlErrorComponent,
  ],
})
export class DateControlComponent extends BaseFormControlDirective {
  private _minValue: moment.Moment | Date | undefined | null;
  private _maxValue: moment.Moment | Date | undefined | null;

  readonly firstDayOfWeek = input<number>(1);
  readonly minDate = input<moment.Moment | Date>(undefined);
  readonly maxDate = input<moment.Moment | Date>(undefined);
  readonly textAlignStyle = model<any>(undefined);

  @Input() set minValue(minDate: moment.Moment | Date | undefined | null) {
    if (this.isDate(minDate)) {
      this._minValue = minDate;
      this.addValidator('dateMinimum', DateValidators.dateMinimum(minDate));
    } else {
      this._minValue = null;
      this.removeValidator('dateMinimum');
    }
  }

  @Input() set maxValue(maxDate: moment.Moment | Date | undefined | null) {
    if (this.isDate(maxDate)) {
      this._maxValue = maxDate;
      this.addValidator('dateMaximum', DateValidators.dateMaximum(maxDate));
    } else {
      this._maxValue = null;
      this.removeValidator('dateMaximum');
    }
  }

  override writeValue(value: any): void {
    const valueAsDate = value ? new Date(value) : undefined;
    if (this.formControl.value?.getTime() !== valueAsDate?.getTime()) {
      this.formControl.setValue(valueAsDate);
    }
  }

  get maxValue() {
    return this._maxValue;
  }

  get minValue() {
    return this._minValue;
  }

  constructor() {
    const cultureService = inject<ICultureService>(CULTURE_SERVICE);
    const injector = inject(Injector);

    super(cultureService, injector);
  }

  clear() {
    if (this.formControl.disabled) return;
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.setValue(null);
  }

  private isDate(date: any) {
    return moment.isDate(date) || moment.isMoment(date);
  }
}
